import type { ApprovalMode } from '../agents-client/agent-client.type';
import type { UseQueryResult } from '@tanstack/react-query';

enum ScheduleType {
  MANUAL = 'MANUAL',
  CRON = 'CRON',
}

enum DeliveryChannelType {
  EMAIL = 'email',
  SLACK = 'slack',
  TEAMS = 'teams',
}

enum SlackDeliveryTarget {
  DM = 'dm',
  CHANNEL = 'channel',
}

enum TeamsDeliveryTarget {
  CHAT = 'chat',
  CHANNEL = 'channel',
}

interface EmailDeliveryChannel {
  type: DeliveryChannelType.EMAIL;
  recipients: string[];
}

interface SlackChannelDelivery {
  type: DeliveryChannelType.SLACK;
  target: SlackDeliveryTarget.CHANNEL;
  team_id: string;
  channel_id: string;
  channel_name?: string;
}

interface SlackDmDelivery {
  type: DeliveryChannelType.SLACK;
  target: SlackDeliveryTarget.DM;
  team_id: string;
  slack_user_id: string;
  user_id?: string;
}

type SlackDeliveryChannel = SlackChannelDelivery | SlackDmDelivery;

interface TeamsChatDelivery {
  type: DeliveryChannelType.TEAMS;
  target: TeamsDeliveryTarget.CHAT;
  tenant_id: string;
  aad_object_id: string;
}

interface TeamsChannelDelivery {
  type: DeliveryChannelType.TEAMS;
  target: TeamsDeliveryTarget.CHANNEL;
  tenant_id: string;
  team_id: string;
  channel_id: string;
  channel_name?: string;
}

type TeamsDeliveryChannel = TeamsChatDelivery | TeamsChannelDelivery;

type DeliveryChannel = EmailDeliveryChannel | SlackDeliveryChannel | TeamsDeliveryChannel;

interface DeliveryAttempt {
  channel: string;
  status: string;
  error: string | null;
  at: string;
  recipients?: string[];
  target?: SlackDeliveryTarget | TeamsDeliveryTarget;
  team_id?: string;
  slack_channel_id?: string;
  channel_name?: string;
  slack_user_id?: string;
  thread_ts?: string | null;
  // Teams attempt fields (mirror the success/failure records in delivery/teams.py).
  tenant_id?: string;
  channel_id?: string;
  aad_object_id?: string;
  chat_id?: string;
  message_id?: string;
}

enum AutomationTriggerType {
  MANUAL = 'MANUAL',
  CRON = 'CRON',
  EVENT = 'EVENT',
}

enum AutomationEventType {
  TARGET_MISSED = 'target_missed',
  UNEXPECTED_TASK_EVENT = 'unexpected_task_event',
  SKIPPED_STATUS = 'skipped_status',
  WORK_PERIOD_STARTED = 'work_period_started',
  WORK_PERIOD_ENDED = 'work_period_ended',
  TASK_CREATED = 'task_created',
  TASK_STATUS_CHANGED = 'task_status_changed',
  TASK_FIELD_CHANGED = 'task_field_changed',
  // Internal trigger surfaced by the filter-definitions endpoint only to
  // Bloomfilter superusers; never returned for customer users.
  FLORA_DEBUG_ERROR = 'flora_debug_error',
}

enum AutomationActionType {
  FLORA_PROMPT = 'FLORA_PROMPT',
  STATIC_MESSAGE = 'STATIC_MESSAGE',
}

enum FilterMode {
  ALL = 'all',
  ANY = 'any',
}

enum FilterOperator {
  IS = 'is',
  IS_NOT = 'is_not',
  IS_ANY_OF = 'is_any_of',
  IS_NONE_OF = 'is_none_of',
  EQUALS = 'equals',
  CONTAINS = 'contains',
  GT = 'gt',
  GTE = 'gte',
  LT = 'lt',
  LTE = 'lte',
  BETWEEN = 'between',
  BEFORE = 'before',
  AFTER = 'after',
  IS_TRUE = 'is_true',
  IS_FALSE = 'is_false',
}

type FilterRuleValue = string | string[] | number | [number, number] | boolean | null;

interface FilterRule {
  field: string;
  operator: FilterOperator | string;
  value?: FilterRuleValue;
}

interface FilterRules {
  mode: FilterMode;
  rules: FilterRule[];
}

interface FilterFieldDefinition {
  field: string;
  label: string;
  value_type: 'uuid' | 'string' | 'number' | 'datetime' | 'boolean';
  operators: string[];
  ui_control: string;
  options_source: string | null;
}

interface TriggerVariable {
  path: string;
  label: string;
  value_type: 'uuid' | 'string' | 'number' | 'datetime' | 'date' | 'boolean';
  example?: string;
  description?: string;
}

interface EventPresetDefinition {
  key: string;
  label: string;
  description: string;
  rules: FilterRule[];
}

interface EventFilterDefinition {
  event_type: AutomationEventType;
  label: string;
  required_filters: string[];
  optional_filters: FilterFieldDefinition[];
  trigger_variables: TriggerVariable[];
  // Optional so an FE ahead of the BE degrades to no presets instead of crashing.
  presets?: EventPresetDefinition[];
}

interface FilterDefinitionsResponse {
  events: EventFilterDefinition[];
}

interface AutomationEmailRecipientSuggestion {
  name: string;
  email: string;
}

interface AutomationEmailRecipientSuggestionsResponse {
  results: AutomationEmailRecipientSuggestion[];
}

interface FloraPromptActionConfig {
  prompt: string;
}

interface StaticMessageActionConfig {
  message: string;
}

type AutomationActionConfig = FloraPromptActionConfig | StaticMessageActionConfig;

interface AutomationWorkflow {
  id: string;
  organization: string;
  user: string | null;
  name: string;
  description: string | null;
  is_active: boolean;
  trigger_type: AutomationTriggerType;
  cron_expression: string | null;
  event_type: AutomationEventType | null;
  filter_rules: FilterRules | Record<string, never>;
  action_type: AutomationActionType;
  action_config: AutomationActionConfig;
  delivery_channels: DeliveryChannel[];
  last_run_at: string | null;
  last_run_status: string | null;
  approval_mode: ApprovalMode | null;
  metadata_created_at: string;
  metadata_updated_at: string;
}

interface AutomationWorkflowWrite {
  organization: string;
  user?: string | null;
  name: string;
  description?: string | null;
  is_active: boolean;
  trigger_type: AutomationTriggerType;
  cron_expression?: string | null;
  event_type: AutomationEventType | null;
  filter_rules?: FilterRules | Record<string, never>;
  action_type: AutomationActionType;
  action_config: AutomationActionConfig;
  delivery_channels: DeliveryChannel[];
  approval_mode?: ApprovalMode | null;
}

enum AutomationRunStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

enum AutomationRunTriggerSource {
  CRON = 'CRON',
  EVENT = 'EVENT',
  MANUAL = 'MANUAL',
  API = 'API',
}

interface AutomationWorkflowRun {
  id: string;
  workflow: string;
  status: AutomationRunStatus;
  trigger_source: AutomationRunTriggerSource;
  output_text: string | null;
  error: string | null;
  // Chat thread id resolved from the run's AgentJob; null for static-message
  // runs (no chat) and runs that haven't produced an AgentJob yet.
  chat_history: string | null;
  delivery_attempts: DeliveryAttempt[];
  started_at: string | null;
  completed_at: string | null;
  metadata_created_at: string;
}

type UseAutomationWorkflowsResult = {
  workflows: AutomationWorkflow[];
  isLoading: boolean;
  error: Error | null;
  refetch: UseQueryResult<AutomationWorkflow[], Error>['refetch'];
};

type UseAutomationWorkflowResult = {
  workflow: AutomationWorkflow | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: UseQueryResult<AutomationWorkflow, Error>['refetch'];
};

type UseAutomationFilterDefinitionsResult = {
  definitions: EventFilterDefinition[];
  isLoading: boolean;
  error: Error | null;
};

interface UseAutomationEmailRecipientSuggestionsResult {
  suggestions: AutomationEmailRecipientSuggestion[];
  isLoading: boolean;
  error: Error | null;
}

type UseAutomationWorkflowRunsResult = {
  runs: AutomationWorkflowRun[];
  isLoading: boolean;
  // True for the initial load AND for any manual refetch — used by the runs
  // view's refresh button to spin during a click-driven reload (`isLoading`
  // only fires on the first load).
  isFetching: boolean;
  error: Error | null;
  refetch: UseQueryResult<AutomationWorkflowRun[], Error>['refetch'];
};

export {
  AutomationActionType,
  AutomationEventType,
  AutomationRunStatus,
  AutomationRunTriggerSource,
  AutomationTriggerType,
  DeliveryChannelType,
  FilterMode,
  FilterOperator,
  ScheduleType,
  SlackDeliveryTarget,
  TeamsDeliveryTarget,
};

export type {
  AutomationActionConfig,
  AutomationEmailRecipientSuggestion,
  AutomationEmailRecipientSuggestionsResponse,
  AutomationWorkflow,
  AutomationWorkflowRun,
  AutomationWorkflowWrite,
  DeliveryAttempt,
  DeliveryChannel,
  EmailDeliveryChannel,
  EventFilterDefinition,
  EventPresetDefinition,
  FilterDefinitionsResponse,
  FilterFieldDefinition,
  FilterRule,
  FilterRules,
  FilterRuleValue,
  SlackDeliveryChannel,
  TeamsDeliveryChannel,
  TriggerVariable,
  UseAutomationEmailRecipientSuggestionsResult,
  UseAutomationFilterDefinitionsResult,
  UseAutomationWorkflowResult,
  UseAutomationWorkflowRunsResult,
  UseAutomationWorkflowsResult,
};
